// Comprehensive filter options for apartment search
// Covers Japanese real estate portal requirements

export interface FilterOption {
  value: string;
  labelKey: string;
}

export interface FilterCategory {
  id: string;
  labelKey: string;
  icon: string;
  filters: FilterConfig[];
}

export interface FilterConfig {
  id: string;
  labelKey: string;
  type: "checkbox" | "select" | "multiselect" | "range" | "radio" | "dualRange";
  options?: FilterOption[];
  min?: number;
  max?: number;
  step?: number;
  unit?: string;
  minId?: string;
  maxId?: string;
  formatValue?: "currency" | "area" | "minutes";
}

// Property Types
export const PROPERTY_TYPES: FilterOption[] = [
  { value: "apartment", labelKey: "filterPropertyApartment" },
  { value: "mansion", labelKey: "filterPropertyMansion" },
  { value: "share_house", labelKey: "filterPropertyShareHouse" },
  { value: "guest_house", labelKey: "filterPropertyGuestHouse" },
  { value: "monthly_mansion", labelKey: "filterPropertyMonthlyMansion" },
];

// Room layouts (Japanese style)
export const ROOM_LAYOUTS: FilterOption[] = [
  { value: "1R", labelKey: "filterLayout1R" },
  { value: "1K", labelKey: "filterLayout1K" },
  { value: "1DK", labelKey: "filterLayout1DK" },
  { value: "1LDK", labelKey: "filterLayout1LDK" },
  { value: "2K", labelKey: "filterLayout2K" },
  { value: "2DK", labelKey: "filterLayout2DK" },
  { value: "2LDK", labelKey: "filterLayout2LDK" },
  { value: "3K", labelKey: "filterLayout3K" },
  { value: "3DK", labelKey: "filterLayout3DK" },
  { value: "3LDK+", labelKey: "filterLayout3LDKPlus" },
];

// Key money options
export const KEY_MONEY_OPTIONS: FilterOption[] = [
  { value: "0", labelKey: "filterNoKeyMoney" },
  { value: "0.5", labelKey: "filterHalfMonth" },
  { value: "1", labelKey: "filter1Month" },
  { value: "any", labelKey: "filterAny" },
];

// Deposit options
export const DEPOSIT_OPTIONS: FilterOption[] = [
  { value: "0", labelKey: "filterNoDeposit" },
  { value: "1", labelKey: "filter1Month" },
  { value: "2", labelKey: "filter2Months" },
  { value: "any", labelKey: "filterAny" },
];

// Building age options
export const BUILDING_AGE_OPTIONS: FilterOption[] = [
  { value: "any", labelKey: "filterAnyAge" },
  { value: "5", labelKey: "filterUnder5Years" },
  { value: "10", labelKey: "filterUnder10Years" },
  { value: "20", labelKey: "filterUnder20Years" },
  { value: "30", labelKey: "filterUnder30Years" },
];

// Floor preferences
export const FLOOR_OPTIONS: FilterOption[] = [
  { value: "any", labelKey: "filterAnyFloor" },
  { value: "1", labelKey: "filterFirstFloor" },
  { value: "2+", labelKey: "filter2ndFloorPlus" },
  { value: "3+", labelKey: "filter3rdFloorPlus" },
  { value: "top", labelKey: "filterTopFloor" },
];

// Walk to station options
export const WALK_TO_STATION_OPTIONS: FilterOption[] = [
  { value: "any", labelKey: "filterAnyWalkTime" },
  { value: "5", labelKey: "filterWithin5Min" },
  { value: "10", labelKey: "filterWithin10Min" },
  { value: "15", labelKey: "filterWithin15Min" },
  { value: "20", labelKey: "filterWithin20Min" },
];

// Building structure options (multiselect)
export const BUILDING_STRUCTURE_OPTIONS: FilterOption[] = [
  { value: "rc", labelKey: "filterStructureRC" },
  { value: "steel", labelKey: "filterStructureSteel" },
  { value: "wood", labelKey: "filterStructureWood" },
  { value: "src", labelKey: "filterStructureSRC" },
];

// Listing freshness options
export const LISTING_FRESHNESS_OPTIONS: FilterOption[] = [
  { value: "any", labelKey: "filterFreshnessAny" },
  { value: "24h", labelKey: "filterFreshness24h" },
  { value: "3d", labelKey: "filterFreshness3d" },
  { value: "7d", labelKey: "filterFreshness7d" },
  { value: "14d", labelKey: "filterFreshness14d" },
];

// All filter categories - reorganized
export const FILTER_CATEGORIES: FilterCategory[] = [
  {
    id: "property",
    labelKey: "filterCategoryProperty",
    icon: "Home",
    filters: [
      {
        id: "property_type",
        labelKey: "filterPropertyType",
        type: "multiselect",
        options: PROPERTY_TYPES,
      },
    ],
  },
  {
    id: "price",
    labelKey: "filterCategoryPrice",
    icon: "Banknote",
    filters: [
      {
        id: "rent",
        labelKey: "filterMinRent",
        type: "dualRange",
        minId: "rent_min",
        maxId: "rent_max",
        min: 0,
        max: 500000,
        step: 10000,
        unit: "¥",
        formatValue: "currency",
      },
      {
        id: "include_management_fee",
        labelKey: "filterIncludeManagementFee",
        type: "checkbox",
      },
      {
        id: "key_money",
        labelKey: "filterKeyMoney",
        type: "select",
        options: KEY_MONEY_OPTIONS,
      },
      {
        id: "deposit",
        labelKey: "filterDeposit",
        type: "select",
        options: DEPOSIT_OPTIONS,
      },
    ],
  },
  {
    id: "size",
    labelKey: "filterCategorySize",
    icon: "LayoutGrid",
    filters: [
      {
        id: "room_layout",
        labelKey: "filterRoomLayout",
        type: "multiselect",
        options: ROOM_LAYOUTS,
      },
      {
        id: "size",
        labelKey: "filterMinSize",
        type: "dualRange",
        minId: "size_min",
        maxId: "size_max",
        min: 0,
        max: 200,
        step: 5,
        unit: "m²",
        formatValue: "area",
      },
    ],
  },
  {
    id: "bathroom",
    labelKey: "filterCategoryBathroom",
    icon: "Bath",
    filters: [
      {
        id: "bath_toilet_separate",
        labelKey: "filterBathToiletSeparate",
        type: "checkbox",
      },
      {
        id: "reheating_bath",
        labelKey: "filterReheatingBath",
        type: "checkbox",
      },
      {
        id: "bathroom_dryer",
        labelKey: "filterBathroomDryer",
        type: "checkbox",
      },
      { id: "washlet_seat", labelKey: "filterWashletSeat", type: "checkbox" },
      {
        id: "independent_vanity",
        labelKey: "filterIndependentVanity",
        type: "checkbox",
      },
      {
        id: "indoor_washer_space",
        labelKey: "filterIndoorWasherSpace",
        type: "checkbox",
      },
      { id: "washer_hookup", labelKey: "filterWasherHookup", type: "checkbox" },
      {
        id: "washer_dryer_in_unit",
        labelKey: "filterWasherDryerInUnit",
        type: "checkbox",
      },
    ],
  },
  {
    id: "kitchen",
    labelKey: "filterCategoryKitchen",
    icon: "ChefHat",
    filters: [
      {
        id: "system_kitchen",
        labelKey: "filterSystemKitchen",
        type: "checkbox",
      },
      {
        id: "counter_kitchen",
        labelKey: "filterCounterKitchen",
        type: "checkbox",
      },
      { id: "gas_stove", labelKey: "filterGasStove", type: "checkbox" },
      { id: "ih_cooktop", labelKey: "filterIHCooktop", type: "checkbox" },
      { id: "dishwasher", labelKey: "filterDishwasher", type: "checkbox" },
    ],
  },
  {
    id: "building",
    labelKey: "filterCategoryBuilding",
    icon: "Building2",
    filters: [
      {
        id: "building_age",
        labelKey: "filterBuildingAge",
        type: "select",
        options: BUILDING_AGE_OPTIONS,
      },
      {
        id: "floor",
        labelKey: "filterFloor",
        type: "select",
        options: FLOOR_OPTIONS,
      },
      {
        id: "building_structure",
        labelKey: "filterBuildingStructure",
        type: "multiselect",
        options: BUILDING_STRUCTURE_OPTIONS,
      },
      { id: "elevator", labelKey: "filterElevator", type: "checkbox" },
      { id: "south_facing", labelKey: "filterSouthFacing", type: "checkbox" },
      { id: "corner_unit", labelKey: "filterCornerUnit", type: "checkbox" },
      { id: "balcony", labelKey: "filterBalcony", type: "checkbox" },
      { id: "flooring_wood", labelKey: "filterFlooringWood", type: "checkbox" },
    ],
  },
  {
    id: "security",
    labelKey: "filterCategorySecurity",
    icon: "Shield",
    filters: [
      { id: "auto_lock", labelKey: "filterAutoLock", type: "checkbox" },
      {
        id: "security_camera",
        labelKey: "filterSecurityCamera",
        type: "checkbox",
      },
      {
        id: "video_intercom",
        labelKey: "filterVideoIntercom",
        type: "checkbox",
      },
      { id: "delivery_box", labelKey: "filterDeliveryBox", type: "checkbox" },
    ],
  },
  {
    id: "amenities",
    labelKey: "filterCategoryAmenities",
    icon: "Star",
    filters: [
      {
        id: "air_conditioning",
        labelKey: "filterAirConditioning",
        type: "checkbox",
      },
      {
        id: "internet_included",
        labelKey: "filterInternetIncluded",
        type: "checkbox",
      },
      { id: "parking", labelKey: "filterParking", type: "checkbox" },
      {
        id: "bicycle_parking",
        labelKey: "filterBicycleParking",
        type: "checkbox",
      },
      {
        id: "motorcycle_parking",
        labelKey: "filterMotorcycleParking",
        type: "checkbox",
      },
    ],
  },
  {
    id: "access",
    labelKey: "filterCategoryAccess",
    icon: "Train",
    filters: [
      {
        id: "walk_to_station",
        labelKey: "filterWalkToStation",
        type: "select",
        options: WALK_TO_STATION_OPTIONS,
      },
    ],
  },
  {
    id: "contract",
    labelKey: "filterCategoryContract",
    icon: "FileText",
    filters: [
      {
        id: "foreigner_friendly",
        labelKey: "filterForeignerFriendly",
        type: "checkbox",
      },
      { id: "pets_allowed", labelKey: "filterPetsAllowed", type: "checkbox" },
      {
        id: "instruments_ok",
        labelKey: "filterInstrumentsOK",
        type: "checkbox",
      },
      { id: "soho_ok", labelKey: "filterSohoOK", type: "checkbox" },
      {
        id: "guarantor_required",
        labelKey: "filterGuarantor",
        type: "checkbox",
      },
      {
        id: "move_in_within_30_days",
        labelKey: "filterMoveInWithin30Days",
        type: "checkbox",
      },
    ],
  },
  {
    id: "listing",
    labelKey: "filterCategoryListing",
    icon: "Clock",
    filters: [
      {
        id: "listing_freshness",
        labelKey: "filterListingFreshness",
        type: "select",
        options: LISTING_FRESHNESS_OPTIONS,
      },
    ],
  },
];

// Default filter state
export interface FilterState {
  property_type: string[];
  rent_min: number;
  rent_max: number;
  include_management_fee: boolean;
  key_money: string;
  deposit: string;
  room_layout: string[];
  size_min: number;
  size_max: number;
  // Bathroom & laundry
  bath_toilet_separate: boolean;
  reheating_bath: boolean;
  bathroom_dryer: boolean;
  washlet_seat: boolean;
  independent_vanity: boolean;
  indoor_washer_space: boolean;
  washer_hookup: boolean;
  washer_dryer_in_unit: boolean;
  // Kitchen
  system_kitchen: boolean;
  counter_kitchen: boolean;
  gas_stove: boolean;
  ih_cooktop: boolean;
  dishwasher: boolean;
  // Building
  building_age: string;
  floor: string;
  building_structure: string[];
  elevator: boolean;
  south_facing: boolean;
  corner_unit: boolean;
  balcony: boolean;
  flooring_wood: boolean;
  // Security
  auto_lock: boolean;
  security_camera: boolean;
  video_intercom: boolean;
  delivery_box: boolean;
  // Amenities
  air_conditioning: boolean;
  internet_included: boolean;
  parking: boolean;
  bicycle_parking: boolean;
  motorcycle_parking: boolean;
  // Access
  walk_to_station: string;
  // Contract
  foreigner_friendly: boolean;
  pets_allowed: boolean;
  instruments_ok: boolean;
  soho_ok: boolean;
  guarantor_required: boolean;
  move_in_within_30_days: boolean;
  // Listing
  listing_freshness: string;
}

export const DEFAULT_FILTER_STATE: FilterState = {
  property_type: [],
  rent_min: 0,
  rent_max: 500000,
  include_management_fee: true,
  key_money: "any",
  deposit: "any",
  room_layout: [],
  size_min: 0,
  size_max: 200,
  // Bathroom & laundry
  bath_toilet_separate: false,
  reheating_bath: false,
  bathroom_dryer: false,
  washlet_seat: false,
  independent_vanity: false,
  indoor_washer_space: false,
  washer_hookup: false,
  washer_dryer_in_unit: false,
  // Kitchen
  system_kitchen: false,
  counter_kitchen: false,
  gas_stove: false,
  ih_cooktop: false,
  dishwasher: false,
  // Building
  building_age: "any",
  floor: "any",
  building_structure: [],
  elevator: false,
  south_facing: false,
  corner_unit: false,
  balcony: false,
  flooring_wood: false,
  // Security
  auto_lock: false,
  security_camera: false,
  video_intercom: false,
  delivery_box: false,
  // Amenities
  air_conditioning: false,
  internet_included: false,
  parking: false,
  bicycle_parking: false,
  motorcycle_parking: false,
  // Access
  walk_to_station: "any",
  // Contract
  foreigner_friendly: false,
  pets_allowed: false,
  instruments_ok: false,
  soho_ok: false,
  guarantor_required: false,
  move_in_within_30_days: false,
  // Listing
  listing_freshness: "any",
};
